import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Send } from "lucide-react";
import api, { apiError, formatRupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function formatWaktu(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function RemindersSection() {
  const { user, schoolId, academicYear } = useAuth();
  const canSend = ["super_admin", "bendahara"].includes(user.role);
  const [unpaid, setUnpaid] = useState(null);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [schoolAy, setSchoolAy] = useState("");

  const load = () => {
    api.get("/reminders/unpaid").then((r) => { setUnpaid(r.data); setSelected(new Set()); })
      .catch((e) => { if (e.response?.status !== 400) toast.error(apiError(e)); setUnpaid([]); });
    api.get("/reminders/history").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/schools/current").then((r) => setSchoolAy(r.data?.academic_year || "")).catch(() => {});
  };

  useEffect(() => {
    if (user.role === "super_admin" && !schoolId) { setUnpaid([]); return; }
    load();
  }, [schoolId, academicYear]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const withPhone = (unpaid || []).filter((u) => u.parent_phone);
  const allSelected = withPhone.length > 0 && withPhone.every((u) => selected.has(u.student_id));

  const send = async (ids) => {
    if (!ids.length) {
      toast.error("Pilih minimal satu siswa yang punya No WA");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/reminders/send", { student_ids: ids });
      toast.success(`Pengingat terkirim ke ${r.data.sent} wali${r.data.skipped ? ` (${r.data.skipped} tanpa No WA)` : ""}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="reminders-section">
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-slate-800">
              <BellRing className="h-4 w-4 text-amber-500" strokeWidth={1.5} /> Pengingat Pembayaran
            </h2>
            <p className="mt-1 text-xs text-slate-500">Wali dengan SPP atau tagihan belum lunas — termasuk bulan-bulan sebelumnya dan lulusan yang masih menunggak. Pesan WA otomatis berisi nama siswa dan rincian tunggakan.</p>
            <div className="mt-2 flex items-center gap-2">
              <Badge className="rounded-full bg-[#E5F0FA] text-[#0060AF] hover:bg-[#E5F0FA]" data-testid="reminders-ayear-label">TA {academicYear || schoolAy || "aktif sekolah"}</Badge>
              <span className="text-[11px] text-slate-400">Mengikuti tahun ajaran yang dipilih di halaman SPP / Tagihan / Tabungan</span>
            </div>
          </div>
          {canSend && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => send([...selected])} disabled={sending || selected.size === 0}
                      data-testid="reminder-send-selected" className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50">
                <Send className="h-3.5 w-3.5" strokeWidth={1.5} /> Kirim ke Terpilih ({selected.size})
              </Button>
              <Button onClick={() => send(withPhone.map((u) => u.student_id))} disabled={sending || withPhone.length === 0}
                      data-testid="reminder-send-all" className="gap-1.5 bg-amber-500 text-white hover:bg-amber-600">
                <Send className="h-3.5 w-3.5" strokeWidth={1.5} /> Kirim ke Semua ({withPhone.length})
              </Button>
            </div>
          )}
        </div>

        <div className="mt-4 overflow-x-auto">
          <Table data-testid="reminders-unpaid-table">
            <TableHeader>
              <TableRow>
                {canSend && (
                  <TableHead className="w-10">
                    <Checkbox checked={allSelected} onCheckedChange={(v) => setSelected(v ? new Set(withPhone.map((u) => u.student_id)) : new Set())}
                              data-testid="reminder-select-all" aria-label="Pilih semua" />
                  </TableHead>
                )}
                <TableHead>Siswa</TableHead>
                <TableHead>Tunggakan</TableHead>
                <TableHead className="text-right">Total Sisa</TableHead>
                <TableHead>No WA Wali</TableHead>
                {canSend && <TableHead className="text-right">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {unpaid === null && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">Memuat data...</TableCell></TableRow>
              )}
              {unpaid !== null && unpaid.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500" data-testid="reminders-empty">Semua siswa sudah lunas. Tidak ada yang perlu diingatkan.</TableCell></TableRow>
              )}
              {(unpaid || []).map((u) => (
                <TableRow key={u.student_id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`reminder-row-${u.student_id}`}>
                  {canSend && (
                    <TableCell>
                      <Checkbox checked={selected.has(u.student_id)} onCheckedChange={() => toggle(u.student_id)}
                                disabled={!u.parent_phone} data-testid={`reminder-check-${u.student_id}`} aria-label={`Pilih ${u.name}`} />
                    </TableCell>
                  )}
                  <TableCell className="text-sm text-slate-800">
                    <span className="font-medium">{u.name}</span>
                    {u.graduated && <Badge className="ml-2 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-100" data-testid={`reminder-graduated-${u.student_id}`}>Lulusan</Badge>}
                    <span className="block text-xs text-slate-400">{u.class_name}</span>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {u.items.map((i) => `${i.label} (${formatRupiah(i.sisa)})`).join(" · ")}
                  </TableCell>
                  <TableCell className="text-right text-sm font-medium text-rose-600">{formatRupiah(u.total_sisa)}</TableCell>
                  <TableCell className="text-sm">
                    {u.parent_phone
                      ? <span className="text-slate-600">{u.parent_phone}</span>
                      : <Badge className="rounded-full bg-slate-100 text-slate-500 hover:bg-slate-100">Belum ada</Badge>}
                  </TableCell>
                  {canSend && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" disabled={sending || !u.parent_phone}
                              onClick={() => send([u.student_id])} data-testid={`reminder-send-${u.student_id}`}
                              className="border-amber-300 text-amber-700 hover:bg-amber-50">
                        Kirim
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="reminders-history-card">
        <h2 className="font-heading text-base font-semibold text-slate-800">Riwayat Pengingat</h2>
        <p className="mt-1 text-xs text-slate-500">Siapa saja yang pernah dikirimi pengingat pembayaran.</p>
        <div className="mt-4 overflow-x-auto">
          <Table data-testid="reminders-history-table">
            <TableHeader>
              <TableRow>
                <TableHead>Waktu</TableHead>
                <TableHead>Nomor Tujuan</TableHead>
                <TableHead>Isi Pesan</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.length === 0 && (
                <TableRow><TableCell colSpan={4} className="py-8 text-center text-sm text-slate-500">Belum ada pengingat yang dikirim.</TableCell></TableRow>
              )}
              {history.map((hItem) => (
                <TableRow key={hItem.id} data-testid={`reminder-history-${hItem.id}`}>
                  <TableCell className="whitespace-nowrap text-sm text-slate-600">{formatWaktu(hItem.created_at)}</TableCell>
                  <TableCell className="text-sm text-slate-800">{hItem.to}</TableCell>
                  <TableCell className="max-w-md truncate text-xs text-slate-500" title={hItem.message}>{hItem.message?.replace(/\n/g, " ").slice(0, 90)}...</TableCell>
                  <TableCell>
                    <Badge className={`rounded-full ${hItem.status === "sent" ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : hItem.status === "failed" ? "bg-rose-50 text-rose-600 hover:bg-rose-50" : "bg-amber-50 text-amber-700 hover:bg-amber-50"}`}>
                      {hItem.status === "sent" ? "Terkirim" : hItem.status === "failed" ? "Gagal" : "Antre"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
